import { AxiosError, HttpStatusCode } from 'axios';
import { Box, Typography } from '@mui/material';
import { FC, Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';

import { useCheckBookingStatus } from 'src/lib/hooks/use-get-check-booking-status';
import { Spinner } from 'src/components/spinner';
import { routes } from 'src/constants/routes';
import { BookingFlow, BookingStatus, ErrorReason } from 'src/shared/enum/BookingStatus';
import { removeStorage } from 'src/utils/local-storage-versioning';
import { getTheme } from 'src/utils/getTheme';
import { useTrackPurchaseEvent } from 'src/lib/hooks/use-track-purchase-event';

import { styles } from './styles';
import { useGetProcessingData } from './hooks/use-get-processing-data';

const { isHosTheme } = getTheme();

const TextWrapper: FC<{ children: ReactNode }> = (props) => {
    const Wrapper = isHosTheme ? Box : Fragment;

    return <Wrapper>{props.children}</Wrapper>;
};

const ProcessingSpinner: FC = () => {
    const { t } = useTranslation();

    return (
        <Box css={styles.container}>
            <Box css={styles.imageContainer} />

            <Box css={styles.wrapper}>
                <TextWrapper>
                    <Typography variant="h1" css={styles.title}>
                        {t('booking_processing')}
                    </Typography>

                    <Typography variant="body3">{t('processing_info')}</Typography>
                </TextWrapper>

                <Spinner />
            </Box>
        </Box>
    );
};

const StandardProcessingPage: FC = () => {
    const BOOKING_RECORD_PARAM = 'booking';
    const [searchParams] = useSearchParams();
    const paramsBookingRecordId = searchParams.get(BOOKING_RECORD_PARAM);

    const trackPurchaseEvent = useTrackPurchaseEvent();
    const { query } = useCheckBookingStatus(paramsBookingRecordId);

    const { error, data, isPending } = query;
    const {
        error: processDataError,
        data: processData,
        isPending: isProcessingData,
    } = useGetProcessingData();

    if (error || processDataError) {
        if (
            error instanceof AxiosError &&
            error.response &&
            error.response.status === HttpStatusCode.NotFound
        ) {
            return <ProcessingSpinner />;
        }

        return <Navigate to={routes.failure.index} />;
    }

    if (isPending || isProcessingData) return <ProcessingSpinner />;
    if (!data || !processData) return <Navigate to={routes.failure.index} />;
    if (
        data.booking_status === BookingStatus.IN_PROGRESS ||
        data.booking_status === BookingStatus.RETRY
    )
        return <ProcessingSpinner />;

    if (data.booking_status === BookingStatus.ERROR) {
        let errorReason;

        if (data.booking_flow === BookingFlow.NON_PAYMENT_BOOKING) {
            errorReason = ErrorReason.BOOKING_WITHOUT_PAYMENT_FAILED;
        } else if (data.booking_flow === BookingFlow.STANDARD_BOOKING) {
            errorReason = ErrorReason.BOOKING_FAILED;
        }

        if (errorReason) {
            return (
                <Navigate
                    to={`${routes.failure.index}?reason=${encodeURIComponent(errorReason)}`}
                />
            );
        }
    }

    const { basket, room, building, userChoices } = processData;
    trackPurchaseEvent(basket, room, building, userChoices);
    removeStorage();
    return <Navigate to={routes.success.index} />;
};

export { StandardProcessingPage as ProcessingPage };

/*
 *
 *       THIS HAS BEEN COMMENTED OUT TO ARCHIVE THE MADRID ORIA FUNCTIONALITY
 *       WE KEPT THIS IN, BUT COMMENTED OUT TO ARCHIVE THE FUNCTIONALITY, SHOULD WE NEED IT IN FUTURE
 *                               TOLA + PEPPER 12-05-26
 *
 */

// export const ProcessingPage: FC = () => {
//     const buildingData = useSelector(selectBuilding);
//     const hasAdvancePaymentFee = useSelector(selectHasAdvancePayment);

//     const madridOriaJourney =
//         hasAdvancePaymentFee &&
//         appConfig.enableOriaAltJourney &&
//         buildingData?.building_slug === 'madrid-oria';

//     if (madridOriaJourney) return <ProcessingPageOria />;

//     return <StandardProcessingPage />;
// };
